import React, { useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import ModalDialog, {
	ModalBody,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import Popup from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import {
	type MessageDescriptorV2 as MessageDescriptor,
	useIntlV2 as useIntl,
} from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { DATE_FORMAT_SLASH } from '../../common/constants';
import ResponsiveButton from '../../common/ui/responsive-button';
import {
	useFireDateFilterSelectedAnalytics,
	useFireFormCancelledAnalytics,
} from '../../common/utils/analytics';
import { formatDate } from '../../common/utils/dates';
import {
	type CustomDateRange,
	useDateFilter,
	type RelativeDateFilterOption,
} from '../../controllers/date-filter';
import DateRangePicker from './date-range-picker';
import KeyboardShortcuts from './keyboard-shortcuts';
import messages from './messages';
import PopupContent from './popup-content';

type RelativeDateFilterMessages = {
	type: 'RELATIVE';
	buttonText: MessageDescriptor;
	tooltipText: MessageDescriptor;
};

type CustomDateFilterMessages = {
	type: 'CUSTOM';
	buttonText: string;
	tooltipText: MessageDescriptor;
	tooltipTextOptions: {
		startDate: string;
		endDate: string;
	};
};

type DateFilterMessages = RelativeDateFilterMessages | CustomDateFilterMessages;

export const getRelativeDateFilterMessages = (
	dateFilterOption: RelativeDateFilterOption,
): DateFilterMessages => {
	switch (dateFilterOption) {
		case '-2w':
			return {
				type: 'RELATIVE',
				buttonText: messages.pastTwoWeeksButtonText,
				tooltipText: messages.pastTwoWeeksTooltipText,
			};
		case '-1m':
			return {
				type: 'RELATIVE',
				buttonText: messages.pastMonthButtonText,
				tooltipText: messages.pastMonthTooltipText,
			};
		case '-3m':
			return {
				type: 'RELATIVE',
				buttonText: messages.pastQuarterButtonText,
				tooltipText: messages.pastQuarterTooltipText,
			};
		case '-1y':
			return {
				type: 'RELATIVE',
				buttonText: messages.pastYearButtonText,
				tooltipText: messages.pastYearTooltipText,
			};
		case 'now':
			throw new Error('Relative date filter value of now should not be selected');
		default:
			throw new Error('No date filter option found when selecting date filter message');
	}
};

export const getCustomDateFilterMessages = ({
	startDate,
	endDate,
}: CustomDateRange): DateFilterMessages => ({
	type: 'CUSTOM',
	buttonText: `${formatDate(startDate, DATE_FORMAT_SLASH)}- ${formatDate(
		endDate,
		DATE_FORMAT_SLASH,
	)}`,
	tooltipText: messages.customRangeTooltipText,
	tooltipTextOptions: {
		startDate: formatDate(startDate, DATE_FORMAT_SLASH),
		endDate: formatDate(endDate, DATE_FORMAT_SLASH),
	},
});

const useFireDateFilterKeyboardShortcutPressedAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return (dateFilterType: string) =>
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'pressed',
				actionSubject: 'keyboardShortcut',
			}),
			'dateFilterKeyboardShortcut',
			{ dateFilterType },
		);
};

interface Props {
	// Used only for VR test to capture opne state
	isModalDefaultOpen?: boolean;
}

const DateFilter = ({ isModalDefaultOpen = false }: Props) => {
	const [isModalOpen, setIsModalOpen] = useState(isModalDefaultOpen);
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [dateFilter] = useDateFilter();
	const { formatMessage } = useIntl();
	const fireDateFilterKeyboardShortCutPressedAnalytics =
		useFireDateFilterKeyboardShortcutPressedAnalytics();
	const fireDateFilterSelectedAnalytics = useFireDateFilterSelectedAnalytics();
	const fireFormCancelledAnalytics = useFireFormCancelledAnalytics();
	const fireKeyboardShortcutAnalytics = (dateFilterType: string) => {
		fireDateFilterKeyboardShortCutPressedAnalytics(dateFilterType);
		fireDateFilterSelectedAnalytics(dateFilterType);
	};

	const closePopup = () => setIsPopupOpen(false);

	const closeModal = () => setIsModalOpen(false);

	const openModal = () => setIsModalOpen(true);

	const dateFilterMessages =
		dateFilter.type === 'CUSTOM'
			? getCustomDateFilterMessages({
					startDate: dateFilter.startDate,
					endDate: dateFilter.endDate,
				})
			: getRelativeDateFilterMessages(dateFilter.startDate);

	return (
		<>
			<KeyboardShortcuts onKeyPress={fireKeyboardShortcutAnalytics} />
			<Popup
				isOpen={isPopupOpen}
				content={() => <PopupContent onOpen={openModal} onClose={closePopup} />}
				onClose={closePopup}
				placement="bottom-start"
				trigger={(triggerProps) => (
					<Tooltip
						content={
							dateFilterMessages.type === 'RELATIVE'
								? formatMessage(dateFilterMessages.tooltipText)
								: formatMessage(
										dateFilterMessages.tooltipText,
										dateFilterMessages.tooltipTextOptions,
									)
						}
						hideTooltipOnClick
						truncate
					>
						<div {...triggerProps}>
							<ResponsiveButton
								isSelected={isPopupOpen}
								onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
									if (!isPopupOpen) {
										fireUIAnalytics(analyticsEvent, 'dateFilterMenuOpen');
									}
									setIsPopupOpen(!isPopupOpen);
								}}
								testId="software-deployments.ui.date-filter.dropdown-menu"
								text={
									dateFilterMessages.type === 'RELATIVE'
										? formatMessage(dateFilterMessages.buttonText)
										: dateFilterMessages.buttonText
								}
								icon={<CalendarIcon label={formatMessage(messages.dateFilterIcon)} />}
							/>
						</div>
					</Tooltip>
				)}
			/>
			<ModalTransition>
				{isModalOpen && (
					<ShortcutScope>
						<ModalDialog
							onClose={() => {
								closeModal();
								fireFormCancelledAnalytics();
							}}
							width="702px"
						>
							<ModalHeader>
								<ModalTitle>{formatMessage(messages.modalHeading)}</ModalTitle>
							</ModalHeader>
							<ModalBody>
								<DateRangePicker onClose={closeModal} />
							</ModalBody>
						</ModalDialog>
					</ShortcutScope>
				)}
			</ModalTransition>
		</>
	);
};

export default DateFilter;
